import type { BadgeTone } from '@zarya/ui';
import { t, tn } from '@app/i18n';
import { riskLabel } from '@app/risk';
import type { components } from '@app/api/types';
import { conditionsText as condicoesTextoDaPolitica } from '@app/main/Policies/PolicyModel';

/** T14 — tipos, defaults do rascunho e vocabulário (FR-071…FR-078). */

export type AccessControlState = components['schemas']['AccessControlState'];
export type ToolRow = AccessControlState['tools'][number];
export type A2ARow = AccessControlState['a2a_out'][number];
export type IncomingRow = AccessControlState['incoming'][number];
export type ViaPolicy = AccessControlState['via_policies'][number];
export type CandidateRequest = components['schemas']['CandidateRequest'];
export type CandidateResult = components['schemas']['CandidateResult'];
export type RiscoExibido = NonNullable<ToolRow['risk']>;

/**
 * Rascunho local. Os limites vivem como **texto** porque o campo aceita vazio
 * ("sem teto") e valor em digitação; a conversão para número só acontece no
 * envio (FR-073).
 */
export interface ToolDraft {
  concedida: boolean;
  limites: Record<string, string>;
}

export interface AccessDraft {
  tools: Record<string, ToolDraft>;
  /** `resource_id` do agente → concedida (fase 2: o alvo é o recurso agente). */
  a2a: Record<string, boolean>;
}

/** Rascunho inicial = o contrato vigente, exatamente como veio. */
export function initialDraft(estado: AccessControlState): AccessDraft {
  return {
    tools: Object.fromEntries(
      estado.tools.map((tool) => [
        tool.tool_id,
        {
          concedida: tool.granted,
          limites: Object.fromEntries(
            tool.constrainable.map((parametro) => [
              parametro,
              tool.constraints?.[parametro] === undefined
                ? ''
                : String(tool.constraints[parametro]),
            ]),
          ),
        },
      ]),
    ),
    a2a: Object.fromEntries(estado.a2a_out.map((alvo) => [alvo.resource_id, alvo.granted])),
  };
}

/**
 * Risco declarado pelo dono do recurso (D4).
 *
 * **Função, e não const**: uma const de módulo é avaliada no import, e o texto
 * congelaria no idioma da primeira renderização — a tela trocaria de idioma e o
 * badge de risco não. O `tone` continua sendo dado, não texto.
 *
 * `read`/`write`/`high` são o vocabulário do risco e valem nos dois idiomas; só
 * «desconhecido» é frase.
 */
const RISK_TONE: Record<RiscoExibido, BadgeTone> = {
  read: 'success',
  write: 'warning',
  high: 'danger',
  desconhecido: 'neutral',
};

/**
 * O rótulo vem de `@app/risk` — **fonte única** do vocabulário de risco no
 * produto (09/09). Ele vivia aqui numa segunda cópia, com texto idêntico ao de
 * `common.risk.*`: nada de errado na tela, e uma revisão de copy que tocasse só
 * uma das duas faria a mesma palavra divergir entre telas.
 *
 * O `tone` fica: cor é desenho, não texto.
 */
export function risk(nivel: RiscoExibido): { rotulo: string; tone: BadgeTone } {
  return { rotulo: riskLabel(nivel), tone: RISK_TONE[nivel] };
}

/** Avisos inline de FR-074 e FR-034 — literais do spec. */
export const warningRiskUnknown = (): string => t('identityAccess.warning.riskUnknown');
export const warningManualResource = (): string => t('identityAccess.warning.manualResource');
/**
 * FR-074, em linguagem de produto (item 10.2 do roteiro do PO, 28/08): a regra e a
 * consequência, sem «estrela» nem «não catalogado» soltos.
 */
export const warningSpecialistA2A = (): string => t('identityAccess.warning.specialistA2A');
export const withoutContract = (): string => t('identityAccess.withoutContract.short');
export const notAcceptsLimit = (): string => t('identityAccess.notAcceptsLimit');

/**
 * O que é uma delegação, na coluna onde ela se marca (DEF-29, 09/09).
 *
 * A frase diz as duas coisas que a aba mudou de uma vez: delegação é capability
 * do contrato (não uma lista à parte) e ela é decidida pelos MESMOS dois eixos
 * de uma tool. A ordem importa — «a pessoa pode E a identidade pode» é a
 * interseção; qualquer redação que sugira soma ou «além do contrato» ressuscita
 * o modelo antigo que o DEF-32 enterrou.
 */
export const delegationTip = (): string => t('identityAccess.delegation.tip');

export type OutsideCatalogRow = AccessControlState['out_of_catalog'][number];

/**
 * O bloco das capabilities que o contrato concede e a grade não desenha (30/08).
 *
 * O título conta **quantas**, porque o número é o que muda a leitura da tela: com
 * a grade toda desmarcada e uma linha aqui, quem olha entende que o agente tem
 * autoridade e que ela não é editável — em vez de concluir que ele não tem
 * nenhuma, que foi o que o laboratório mostrou em duas identidades.
 *
 * # O TÍTULO NÃO DIZ MAIS «FORA DO CATÁLOGO», E ISSO É CORREÇÃO DE DEFEITO
 *
 * A primeira versão chamava todas de «fora do catálogo» e explicava «o catálogo
 * local não as conhece». No laboratório saiu **falso**: o contrato guardava
 * `mcp/incidentes.listar` — o `apim_id` que a release declarou — e o catálogo tinha
 * a tool em `api`. A própria tela desmentia o aviso três linhas abaixo, listando
 * `incidentes.listar` sob `api`, e o remédio que a frase sugeria («importe o
 * resource») não resolvia nada, porque o resource já estava importado.
 *
 * Uma tela que se contradiz e manda fazer o que não resolve é pior do que a que só
 * omitia — antes ela calava, agora ela afirmava. O título passou a dizer o que é
 * **certo nos dois casos**: a grade não consegue mostrar estas capabilities. O
 * porquê, que é diferente em cada uma, vai na linha.
 */
export const titleOutsideCatalog = (n: number): string =>
  tn('identityAccess.outsideCatalog.title', n);

/**
 * A explicação **por linha** — é aqui que os dois casos se separam.
 *
 * Com dono no catálogo: a tool existe, sob outro resource; não há o que importar, e
 * a próxima emissão de contrato corrige sozinha (a resolução pelo catálogo entrou
 * em 30/08). Sem dono: aí sim o catálogo não a conhece, e importar é o caminho.
 */
export function reasonOutsideCatalog(linha: OutsideCatalogRow): string {
  if (linha.owner_in_catalog) {
    return t('identityAccess.outsideCatalog.reason.owner', { owner: linha.owner_in_catalog });
  }
  return t('identityAccess.outsideCatalog.reason.noOwner');
}

/**
 * O recibo da resolução pelo catálogo (30/08), na linha da tool.
 *
 * Desde 30/08 o produto resolve o dono da tool pelo catálogo quando o `apim_id`
 * declarado não a contém, e manda ao executor o dono resolvido. É lookup, não
 * invenção — mas é uma diferença entre o `agent.yaml` do cliente e o que o PDP
 * recebeu, e num produto de segurança essa diferença **tem de estar escrita onde a
 * autoridade está**. Sem isso, um auditor que comparasse os dois lados acharia uma
 * divergência que ninguém explicou.
 *
 * O texto nomeia o par declarado, que é o que a pessoa tem na mão quando pergunta.
 */
export const resolutionByCatalog = (declaradaComo: string): string =>
  t('identityAccess.resolution.declaredAs', { declared: declaradaComo });

export const resolutionTip = (): string => t('identityAccess.resolution.tip');

/**
 * O texto do bloco. Diz só o que vale para **todas** — o motivo de cada uma vai na
 * linha (`motivoForaDoCatalogo`), e o remédio depende dele: importar o resource só
 * resolve quando o catálogo realmente não conhece a tool.
 */
export const warningOutsideCatalog = (): string => t('identityAccess.outsideCatalog.body');

/**
 * `subject_type em [employee] · caller_type em [employee]` — as condições de uma
 * política, na forma canônica (D-7c).
 *
 * Delega a `condicoesTextoDaPolitica` de T11/T12, que também é o rótulo que o
 * backend escreve para as arestas de T19: a coluna «Condições» desta aba tem de
 * ler igual à da tela de políticas, senão a mesma política aparece com dois
 * textos em duas telas. O açúcar deprecado das políticas antigas entra convertido
 * lá, num lugar só.
 */
export function conditionsText(condicoes: ViaPolicy['conditions']): string {
  return condicoesTextoDaPolitica(condicoes);
}

/** `max_rows 100` — limites de uma capability concedida por política. */
export function limitsText(constraints: ViaPolicy['constraints']): string {
  const itens = Object.entries(constraints ?? {});
  return itens.length
    ? itens.map(([chave, valor]) => `${chave} ${valor}`).join(' · ')
    : t('identityAccess.viaPolicies.none');
}

/** Uma tool só é editável pelo par resource + tool: é a chave do PDP. */
export function toolLabel(tool: ToolRow): string {
  return `${tool.resource_gateway_id}/${tool.tool_name}`;
}

/**
 * O que o toast diz depois de Salvar (FR-078, nota de 29/08).
 *
 * São três frases, e o que as separa é **o que aconteceu**, não o que foi pedido:
 *
 *   - pedido que acrescenta autoridade → «Contrato candidato enviado…»;
 *   - **redução** que ficou na fila (produção) → «Redução enviada para aprovação»;
 *   - **redução** que a plataforma já aplicou (dev/hmg) → «…contrato v{N}».
 *
 * A terceira lê `approval.status`, e não `reducao` sozinho: quem decide se a
 * plataforma fecha é o ambiente da identidade, que esta tela não conhece.
 * Adivinhar aqui faria o toast anunciar um contrato que não nasceu — o tipo de
 * afirmação que T14 existe para não fazer ("Salvar não aplica nada").
 */
export function sendMessage(resultado: CandidateResult): string {
  if (!resultado.reduction) {
    const n = resultado.diff?.changes?.length ?? 0;
    return tn('identityAccess.send.candidate', n);
  }
  if (resultado.approval?.status !== 'auto') {
    return t('identityAccess.send.reduction.queued');
  }
  const versao = resultado.approval.resulting_contract_version;
  return versao
    ? t('identityAccess.send.reduction.applied', { version: versao })
    : t('identityAccess.send.reduction.appliedNoVersion');
}
